import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePhotoMetadata } from '@/hooks/usePhotoMetadata';
import { usePhotoUrls } from '@/hooks/usePhotoUrls';
import { useAddPhotosToAlbum } from '@/hooks/useAlbums';
import { cn } from '@/lib/utils';

interface AddPhotosToAlbumDialogProps {
  albumId: string;
  albumName: string;
  existingPhotoIds?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddPhotosToAlbumDialog({
  albumId,
  albumName,
  existingPhotoIds = [],
  open,
  onOpenChange,
}: AddPhotosToAlbumDialogProps) {
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const { data: photos, isLoading } = usePhotoMetadata();
  const addPhotos = useAddPhotosToAlbum(albumId);

  // Filter out photos already in album
  const availablePhotos = useMemo(() => {
    if (!photos) return [];
    const existingSet = new Set(existingPhotoIds);
    return photos.filter((photo) => !existingSet.has(photo.id));
  }, [photos, existingPhotoIds]);

  // Get photo IDs for URL fetching
  const photoIds = useMemo(() => availablePhotos.map((p) => p.id), [availablePhotos]);
  const { data: photoUrls } = usePhotoUrls(photoIds);

  const togglePhoto = (photoId: string) => {
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedPhotoIds.size === 0) {
      toast.error('写真を1枚以上選択してください');
      return;
    }

    try {
      await addPhotos.mutateAsync({
        photoIds: Array.from(selectedPhotoIds),
      });

      toast.success(`${selectedPhotoIds.size}枚の写真をアルバムに追加しました`);
      setSelectedPhotoIds(new Set());
      onOpenChange(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '写真の追加に失敗しました';
      toast.error(errorMessage);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedPhotoIds(new Set());
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>アルバムに写真を追加</DialogTitle>
          <DialogDescription>
            「{albumName}」に追加する写真を選択してください。{' '}
            {selectedPhotoIds.size > 0 && (
              <span className="font-medium text-primary">{selectedPhotoIds.size}枚選択中</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="custom-scrollbar flex-1 overflow-y-auto py-4">
          {isLoading ? (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : availablePhotos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground">
                {!photos || photos.length === 0
                  ? '写真がありません。先に写真をアップロードしてください。'
                  : 'すべての写真がこのアルバムに追加済みです。'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {availablePhotos.map((photo) => (
                <PhotoSelectItem
                  key={photo.id}
                  originalFilename={photo.originalFilename}
                  thumbnailUrl={photoUrls?.[photo.id]?.thumbnailUrl}
                  selected={selectedPhotoIds.has(photo.id)}
                  onToggle={() => togglePhoto(photo.id)}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addPhotos.isPending || selectedPhotoIds.size === 0}
          >
            {addPhotos.isPending ? '追加中...' : `${selectedPhotoIds.size}枚の写真を追加`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PhotoSelectItemProps {
  originalFilename: string | null;
  thumbnailUrl?: string;
  selected: boolean;
  onToggle: () => void;
}

function PhotoSelectItem({
  originalFilename,
  thumbnailUrl,
  selected,
  onToggle,
}: PhotoSelectItemProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'relative aspect-square overflow-hidden rounded-md border-2 transition-all',
        selected
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-transparent hover:border-muted-foreground/30'
      )}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={originalFilename || '写真'}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full bg-muted animate-pulse" />
      )}
      {selected && (
        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
          <div className="rounded-full bg-primary p-1">
            <Check className="h-4 w-4 text-primary-foreground" />
          </div>
        </div>
      )}
    </button>
  );
}
